import { ApiService } from "./apiService";
import {
  GroupedMeasurementsApiResponse,
  GroupedMeasurementsMeta,
  MeasurementLogFilters,
  UserMeasurementLogsApiResponse,
  UserMeasurementLog,
} from "../types/measurement";
import { unwrapPaginated } from "../utils/paginationHelper";
import { PaginatedResult } from "../types/api";
import { logger } from "../utils/logger";

export class MeasurementService {
  static async getUserMeasurementLogs(
    userId: number,
    page: number = 1,
    filters: MeasurementLogFilters = {},
    perPage: number = 10
  ): Promise<PaginatedResult<UserMeasurementLog> | null> {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("per_page", perPage.toString());

      // Empty values fail the API's date_format / Rule::in validation
      if (filters.measurement_type) {
        params.append("measurement_type", filters.measurement_type);
      }
      if (filters.measured_at) {
        params.append("measured_at", filters.measured_at);
      }

      const response = await ApiService.get(
        `/user-measurement-logs/user/${userId}/filter?${params.toString()}`
      );
      const result =
        await ApiService.handleResponse<UserMeasurementLogsApiResponse>(
          response
        );

      return unwrapPaginated<UserMeasurementLog>(result, "measurement logs");
    } catch (err: any) {
      logger.error("Error fetching user measurement logs:", err);
      return null;
    }
  }

  /**
   * Date-grouped history from /grouped: page N is the N-th slice of DATES,
   * newest first, with value=0 placeholders already excluded server-side.
   * The response is not double-wrapped, so meta sits next to data. Rows are
   * returned flattened - the hook regroups them through the same tested
   * grouping util the single-day view uses.
   */
  static async getUserMeasurementsGroupedByDate(
    userId: number,
    page: number = 1,
    perPage: number = 10,
    measurementType?: string
  ): Promise<{
    rows: UserMeasurementLog[];
    meta: GroupedMeasurementsMeta;
  } | null> {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("per_page", perPage.toString());

      // An empty type fails the API's Rule::in validation
      if (measurementType) {
        params.append("type", measurementType);
      }

      const response = await ApiService.get(
        `/user-measurement-logs/user/${userId}/grouped?${params.toString()}`
      );
      const result =
        await ApiService.handleResponse<GroupedMeasurementsApiResponse>(
          response
        );

      if (!result || typeof result !== "object" || !result.meta) {
        logger.error(
          "Unexpected grouped measurements API response format:",
          result
        );
        return null;
      }

      // PHP serializes an empty date set as [] rather than {}
      const rows = Array.isArray(result.data)
        ? []
        : Object.values(result.data).flat();

      return { rows, meta: result.meta };
    } catch (err: any) {
      logger.error("Error fetching grouped measurements:", err);
      return null;
    }
  }
}
